import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SitzungViewConfig from "../components/session/SitzungViewConfig";
import { getFullTranscriptionByIdAsync } from "../redux/transcription";
import { useFooter } from "../components/layout/FooterContext";
import { getAllTranscriptionsAsync } from "../redux/transcription";

const buildOpenTabsFromStore = (transcriptions) => {
  if (typeof window === "undefined") return [];
  if (!window.selectedItemsStore) {
    window.selectedItemsStore = new Map();
  }

  const entries = Array.from(window.selectedItemsStore.entries());
  return entries
    .filter(([_, items]) => Array.isArray(items) && items.length > 0)
    .map(([sessionId]) => {
      const session = (transcriptions || []).find(
        (s) => String(s.id) === String(sessionId)
      );
      return {
        id: String(sessionId),
        sessionId: String(sessionId),
        label: session?.name || `Session ${sessionId}`
      };
    });
};

const Summarizer = () => {
  const dispatch = useDispatch();
  const { setTitle, setHeader } = useFooter();
  const { transcriptions, loading } = useSelector((state) => state.transcriptions);
  const fullTranscriptionsById = useSelector(
    (state) => state.transcriptions.fullTranscriptionsById || {}
  );
  const [openTabs, setOpenTabs] = useState(() => buildOpenTabsFromStore(transcriptions));

  const refreshTabs = useCallback(() => {
    setOpenTabs(buildOpenTabsFromStore(transcriptions));
  }, [transcriptions]);

  useEffect(() => {
    refreshTabs();
  }, [refreshTabs]);

  useEffect(() => {
    dispatch(getAllTranscriptionsAsync());
  }, [dispatch]);

  useEffect(() => {
    setTitle("Summarizer");
    setHeader(null);
  }, [setTitle, setHeader]);

  useEffect(() => {
    const handleUpdate = () => {
      refreshTabs();
    };
    window.addEventListener("selected-items-store-updated", handleUpdate);
    return () => window.removeEventListener("selected-items-store-updated", handleUpdate);
  }, [refreshTabs]);

  const parseDurationToSeconds = useCallback((value) => {
    if (value == null) return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value !== "string") return 0;
    const trimmed = value.trim();
    if (!trimmed) return 0;
    if (/^\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
    const match = trimmed.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
    if (!match) return 0;
    const hours = Number(match[1] || 0);
    const minutes = Number(match[2] || 0);
    const seconds = Number(match[3] || 0);
    return hours * 3600 + minutes * 60 + seconds;
  }, []);

  const buildSessionData = useCallback(
    (fullTranscription) => {
      const normalizedParagraphs = (fullTranscription?.paragraphs || [])
        .map((p) => {
          const startSeconds = parseDurationToSeconds(p.start);
          const durationSeconds = parseDurationToSeconds(p.end);
          const endSeconds =
            Number.isFinite(durationSeconds) && durationSeconds > 0
              ? startSeconds + durationSeconds
              : null;
          return {
            ...p,
            type: "paragraph",
            start: Number.isFinite(startSeconds) ? startSeconds : 0,
            end: Number.isFinite(endSeconds) ? endSeconds : p.end,
            duration_seconds: Number.isFinite(durationSeconds) ? durationSeconds : 0,
          };
        })
        .sort((a, b) => (a.start ?? 0) - (b.start ?? 0));

      const speakerMap = new Map();
      for (const p of normalizedParagraphs) {
        const name = p?.speaker_name ?? "no";
        if (!speakerMap.has(name)) {
          speakerMap.set(name, p?.speaker_number);
        }
      }
      const speakersList = Array.from(speakerMap.entries()).map(([name, num]) => ({
        key: name,
        label: name === "no" ? `Sprecher ${num ?? ""}`.trim() : name,
        value: Number.isFinite(Number(num)) ? Number(num) : undefined,
        speaker_name: name,
        speaker_number: num,
      }));

      return { speakers: speakersList, paragraphChildren: normalizedParagraphs };
    },
    [parseDurationToSeconds]
  );

  const openSessionIds = useMemo(
    () => (openTabs || []).map((tab) => String(tab.sessionId)).filter(Boolean),
    [openTabs]
  );

  const loadingSessionIds = useMemo(() => {
    const pending = new Set();
    openSessionIds.forEach((sessionId) => {
      if (!fullTranscriptionsById[sessionId]) {
        pending.add(String(sessionId));
      }
    });
    return pending;
  }, [fullTranscriptionsById, openSessionIds]);

  // Ensure full transcriptions are loaded for the selected sessions
  useEffect(() => {
    openSessionIds.forEach((sessionId) => {
      const existing = fullTranscriptionsById[sessionId];
      if (!existing) {
        dispatch(getFullTranscriptionByIdAsync(sessionId));
      }
    });
  }, [dispatch, fullTranscriptionsById, openSessionIds]);

  // Populate global sessionDataStore for SitzungViewConfig
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.sessionDataStore) {
      window.sessionDataStore = new Map();
    }
    openSessionIds.forEach((sessionId) => {
      const full = fullTranscriptionsById[sessionId];
      if (full) {
        const data = buildSessionData(full);
        window.sessionDataStore.set(String(sessionId), data);
      }
    });
  }, [buildSessionData, fullTranscriptionsById, openSessionIds]);

  return (
    <div className="h-full w-full">
      <SitzungViewConfig
        localNavData={[]}
        speakers={[]}
        paragraphChildren={[]}
        sessionId={null}
        openTabs={openTabs}
        hideHeader={true}
        initialView="topics"
        loadingSelectedSessionIds={loadingSessionIds}
        isLoading={loading}
      />
    </div>
  );
};

export default Summarizer;
